import {
   ChangeDetectorRef,
   Component,
   ElementRef,
   HostBinding,
   Input,
   OnChanges,
   OnDestroy,
   OnInit,
   Renderer2,
   SimpleChanges
} from '@angular/core';
import { DefaultTo } from '../defaultTo.decorator';

@Component({
   selector: 'app-loader',
   template: `<div class="app-loader-overlay" [class]="'mode-' + mode" *ngIf="isVisible">
      <div class="loading">
         <div class="bar1"></div>
         <div class="bar2"></div>
         <div class="bar3"></div>
         <div class="bar4"></div>
      </div>
   </div>`,
   styleUrls: ['./app-loader.component.scss']
})
export class AppLoaderComponent implements OnInit, OnDestroy, OnChanges {
   @HostBinding('class') get className() {
      return 'app-loader';
   }

   @Input() @DefaultTo('fullscreen') mode: 'fit-container' | 'fullscreen' = 'fullscreen';
   @Input() targetElement: HTMLElement;
   private resizeObserver: ResizeObserver;
   private mutationObserver: MutationObserver;

   protected isVisible = false;

   constructor(private el: ElementRef, private renderer: Renderer2, private cdr: ChangeDetectorRef) {}

   ngOnInit() {
      this.initializeObservers();
   }

   ngOnChanges(change: SimpleChanges): void {
      if (change?.targetElement) {
         this.initializeObservers();
      }
   }

   ngOnDestroy(): void {
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
   }

   show(): void {
      this.isVisible = true;
      this.cdr.detectChanges();
   }

   hide(): void {
      this.isVisible = false;
      this.cdr.detectChanges();
   }

   get visible(): boolean {
      return this.isVisible;
   }

   /**
    * (For mode: 'fit-container' only)
    *
    * Detect style changes in the target element and update the loader style accordingly
    */
   private initializeObservers(): void {
      if (this.mode !== 'fit-container') return;

      this.updateLoaderStyle();

      this.resizeObserver = new ResizeObserver(() => {
         this.updateLoaderStyle();
      });

      this.mutationObserver = new MutationObserver(() => {
         this.updateLoaderStyle();
      });

      this.resizeObserver.observe(this.targetElement);
      this.mutationObserver.observe(this.targetElement, { attributes: true, childList: true, subtree: true });
   }

   private updateLoaderStyle(): void {
      if (this.mode !== 'fit-container') return;

      if (!this.targetElement) return;

      const loader = this.el.nativeElement.querySelector('.mode-fit-container');
      if (!loader) return;

      const rect = this.targetElement.getBoundingClientRect();
      if (!rect) return;

      this.renderer?.setStyle(loader, 'height', `${rect.height}px`);
      this.renderer?.setStyle(loader, 'width', `${rect.width}px`);
      this.renderer?.setStyle(loader, 'top', `${rect.top}px`);
      this.renderer?.setStyle(loader, 'left', `${rect.left}px`);
      this.renderer?.setStyle(loader, 'bottom', `${rect.bottom}px`);
      this.renderer?.setStyle(loader, 'right', `${rect.right}px`);
      this.renderer?.setStyle(loader, 'x', `${rect.x}px`);
      this.renderer?.setStyle(loader, 'y', `${rect.y}px`);
   }
}
